import { useState, useEffect, useMemo, Fragment, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, Upload, Filter, Eye, RotateCcw, ArrowLeft, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { companyApi, Company, personApi, Person, PersonStatsFilters } from '../services/api.ts';
import { parseExcelFile, convertToCompanyData } from '../services/importService.ts';
import { SimpleBarChart, SimplePieChart } from '../components/ChartHelpers.tsx';
import ViewToggle, { ViewMode } from '../components/ViewToggle.tsx';

export default function CompanyList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [columnFilters, setColumnFilters] = useState<{ state: string[]; entity_jurisdiction: string[]; entity_type: string[]; category: string[]; nickname: string[]; status: string[] }>({ state: [], entity_jurisdiction: [], entity_type: [], category: [], nickname: [], status: [] });
  const [activeFilterCol, setActiveFilterCol] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [viewingCompany, setViewingCompany] = useState<Company | null>(null);
  const [showTrash, setShowTrash] = useState(false);
  const [deletedCompanies, setDeletedCompanies] = useState<Company[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [mergedExpandedGroup, setMergedExpandedGroup] = useState<string | null>(null);
  const [mergedExpandedClass, setMergedExpandedClass] = useState<string | null>(null);
  const [mergedExpandedSub, setMergedExpandedSub] = useState<string | null>(null);
  const [entityScope, setEntityScope] = useState<string[]>(['Domestic', 'Foreign', 'Other']);
  
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const STATE_KEY = 'companyListState';
  const FROM_PAGE_KEY = 'companyList_fromPage';

  const saveStateRef = useRef<() => void>(() => {});
  const setFromPageRef = useRef<() => void>(() => {});
  const restoredRef = useRef(false);
  const restoringRef = useRef(false);

  const saveState = () => {
    const state = {
      searchTerm,
      columnFilters,
      currentPage,
      pageSize,
      viewMode,
      personSearchTerm,
      personRoleFilter,
      personLinkedCompaniesFilter,
      personGroupFilter,
    };
    sessionStorage.setItem(STATE_KEY, JSON.stringify(state));
  };
  saveStateRef.current = saveState;

  const setFromPage = () => {
    const currentPath = location.pathname + location.search;
    sessionStorage.setItem(FROM_PAGE_KEY, currentPath);
  };
  setFromPageRef.current = setFromPage;

  const restoreState = () => {
    if (restoredRef.current) return;
    const saved = sessionStorage.getItem(STATE_KEY);
    if (!saved) return;
    try {
      const s = JSON.parse(saved);
      restoringRef.current = true;
      if (s.searchTerm !== undefined) setSearchTerm(s.searchTerm);
      if (s.columnFilters) {
        setColumnFilters({
          state: s.columnFilters.state ?? s.filterStates ?? [],
          entity_jurisdiction: s.columnFilters.entity_jurisdiction ?? [],
          entity_type: s.columnFilters.entity_type ?? [],
          category: s.columnFilters.category ?? [],
          nickname: s.columnFilters.nickname ?? [],
          status: s.columnFilters.status ?? s.filterStatuses ?? [],
        });
      } else {
        setColumnFilters(cf => ({
          ...cf,
          state: s.filterStates ?? cf.state,
          status: s.filterStatuses ?? cf.status,
        }));
      }
      if (s.currentPage) setCurrentPage(s.currentPage);
      if (s.pageSize) setPageSize(s.pageSize);
      if (s.viewMode) setViewMode(s.viewMode);
      if (s.personSearchTerm !== undefined) setPersonSearchTerm(s.personSearchTerm);
      if (s.personRoleFilter !== undefined) setPersonRoleFilter(s.personRoleFilter);
      if (s.personLinkedCompaniesFilter !== undefined) setPersonLinkedCompaniesFilter(s.personLinkedCompaniesFilter);
      if (s.personGroupFilter !== undefined) setPersonGroupFilter(s.personGroupFilter);
      restoredRef.current = true;
      setTimeout(() => { restoringRef.current = false; }, 100);
    } catch {}
  };

  useEffect(() => {
    restoredRef.current = false;
    restoreState();
    sessionStorage.setItem(FROM_PAGE_KEY, location.pathname + location.search);
    fetchCompanies();
    return () => { saveStateRef.current(); };
  }, [location.pathname]);

  useEffect(() => {
    let result = [...companies];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(company => 
        company.company_name?.toLowerCase().includes(term) ||
        company.nickname?.toLowerCase().includes(term) ||
        company.state?.toLowerCase().includes(term)
      );
    }

    const colF = columnFilters;
    const hasSearch = !!searchTerm;

    // 預設排除 Terminated/Cancelled（除非用戶有搜索或 Status 篩選被主動選取）
    if (colF.status.length === 0 && !hasSearch) {
      result = result.filter(c => {
        const s = (c.status || '').toLowerCase();
        return s !== 'terminated' && s !== 'cancelled';
      });
    }

    if (colF.state.length) result = result.filter(c => colF.state.includes(c.state || ''));
    if (colF.entity_jurisdiction.length) result = result.filter(c => colF.entity_jurisdiction.includes(c.entity_jurisdiction || ''));
    if (colF.entity_type.length) result = result.filter(c => colF.entity_type.includes(c.entity_type || ''));
    if (colF.category.length) result = result.filter(c => colF.category.includes(c.category || ''));
    if (colF.nickname.length) result = result.filter(c => colF.nickname.includes(c.nickname || ''));
    if (colF.status.length) result = result.filter(c => colF.status.includes(c.status || ''));
    
    setFilteredCompanies(result);
  }, [companies, searchTerm, columnFilters]);

  // 分页计算
  const totalPages = Math.ceil(filteredCompanies.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedCompanies = filteredCompanies.slice(startIndex, endIndex);

  // 当筛选条件变化时重置页码
  useEffect(() => {
    if (restoringRef.current) return;
    setCurrentPage(1);
  }, [searchTerm, columnFilters, pageSize]);

  useEffect(() => {
    if (!activeFilterCol) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-filter-popup]')) {
        setActiveFilterCol(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeFilterCol]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await companyApi.getAll();
      const data = response.data;
      setCompanies(data);
      if (!restoredRef.current) {
        const allStatuses = Array.from(new Set(data.map((c: Company) => c.status).filter(Boolean)));
        const defaultStatuses = allStatuses.filter((s: string) => s !== 'Terminated');
        setColumnFilters(cf => ({ ...cf, status: defaultStatuses }));
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const deleted_reason = window.prompt('Enter deletion reason:');
    if (deleted_reason !== null) {
      try {
        await companyApi.delete(id, deleted_reason || undefined);
        // 从显示列表中移除
        setCompanies(companies.filter(c => c.id !== id));
        setFilteredCompanies(filteredCompanies.filter(c => c.id !== id));
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  const fetchDeletedCompanies = async () => {
    try {
      const response = await companyApi.getDeleted();
      setDeletedCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch deleted companies:', error);
    }
  };

  const handleRestore = async (id: number) => {
    try {
      await companyApi.restore(id);
      setDeletedCompanies(deletedCompanies.filter(c => c.id !== id));
      fetchCompanies();
    } catch (error) {
      console.error('Restore failed:', error);
    }
  };

  const handlePermanentDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to permanently delete this company? This action cannot be undone.')) {
      try {
        await companyApi.permanentDelete(id);
        setDeletedCompanies(deletedCompanies.filter(c => c.id !== id));
      } catch (error) {
        console.error('Permanent delete failed:', error);
      }
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const { sheets, data } = await parseExcelFile(file);
      
      if (sheets.length === 0) {
        alert('No worksheet found');
        return;
      }
      
      const firstSheet = sheets[0];
      const rawData = data[firstSheet];
      
      if (rawData.length === 0) {
        alert('No data found in worksheet');
        return;
      }
      
      const companyDataList = convertToCompanyData(rawData);
      
      if (companyDataList.length === 0) {
        alert(t('company.importError'));
        return;
      }
      
      for (const companyData of companyDataList) {
        try {
          await companyApi.create(companyData as Omit<Company, 'id' | 'created_at' | 'updated_at'>);
        } catch (error) {
          console.error('Import failed:', error);
        }
      }
      
      await fetchCompanies();
      alert(t('company.importSuccess', { count: companyDataList.length }));
    } catch (error) {
      console.error('Import failed:', error);
      alert('Import failed. Please ensure the file format is correct.');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await companyApi.export();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `companies_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Inactive': return 'bg-blue-100 text-blue-800';
      case 'Terminated': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const uniqueStates = useMemo(() =>
    Array.from(new Set(companies.map(c => c.state).filter(Boolean))).sort(),
    [companies]
  );
  const uniqueEntityTypes = useMemo(() =>
    Array.from(new Set(companies.map(c => c.entity_type).filter(Boolean))).sort(),
    [companies]
  );
  const uniqueEntityJurisdictions = useMemo(() =>
    Array.from(new Set(companies.map(c => c.entity_jurisdiction).filter(Boolean))).sort(),
    [companies]
  );
  const uniqueCategories = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of companies) {
      const cat = (c.category || '').trim();
      if (!cat) continue;
      const key = cat.toLowerCase();
      if (!seen.has(key)) seen.set(key, cat);
    }
    return Array.from(seen.values()).sort();
  }, [companies]);
  const uniqueStatusValues = useMemo(() =>
    Array.from(new Set(companies.map(c => c.status).filter(Boolean))).sort(),
    [companies]
  );
  const uniqueNicknames = useMemo(() =>
    Array.from(new Set(companies.map(c => c.nickname).filter(Boolean))).sort(),
    [companies]
  );

  const stateCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of companies) if (c.state) m.set(c.state, (m.get(c.state) || 0) + 1);
    return m;
  }, [companies]);
  const entityTypeCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of companies) if (c.entity_type) m.set(c.entity_type, (m.get(c.entity_type) || 0) + 1);
    return m;
  }, [companies]);
  const entityJurisdictionCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of companies) if (c.entity_jurisdiction) m.set(c.entity_jurisdiction, (m.get(c.entity_jurisdiction) || 0) + 1);
    return m;
  }, [companies]);
  const categoryCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of companies) if (c.category) m.set(c.category, (m.get(c.category) || 0) + 1);
    return m;
  }, [companies]);
  const statusCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of companies) if (c.status) m.set(c.status, (m.get(c.status) || 0) + 1);
    return m;
  }, [companies]);
  const nicknameCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of companies) if (c.nickname) m.set(c.nickname, (m.get(c.nickname) || 0) + 1);
    return m;
  }, [companies]);
  const getColumnCount = (key: string, val: string): number => {
    if (key === 'state') return stateCounts.get(val) || 0;
    if (key === 'entity_jurisdiction') return entityJurisdictionCounts.get(val) || 0;
    if (key === 'entity_type') return entityTypeCounts.get(val) || 0;
    if (key === 'category') return categoryCounts.get(val) || 0;
    if (key === 'nickname') return nicknameCounts.get(val) || 0;
    if (key === 'status') return statusCounts.get(val) || 0;
    return 0;
  };

  // === Helper: determine top-level category from entity_jurisdiction field ===
  const getTopCategory = (c: Company): string => {
    return (c.entity_jurisdiction || 'Other').trim();
  };

  const getSubType = (c: Company): string => {
    return (c.entity_type || '').trim();
  };

  const getGroupStats = (list: Company[]) => {
    // Count by unique entity_id, not by row
    const entityIds = new Set<string>();
    for (const c of list) {
      const eid = (c.entity_id || String(c.id)).trim();
      entityIds.add(eid);
    }
    const total = entityIds.size;
    
    // Active entities (unique)
    const activeEntityIds = new Set<string>();
    const domesticEntityIds = new Set<string>();
    const foreignEntityIds = new Set<string>();
    const otherEntityIds = new Set<string>();
    const entityTypeMap = new Map<string, number>();
    
    for (const c of list) {
      const eid = (c.entity_id || String(c.id)).trim();
      const s = (c.status || '').toLowerCase();
      if (s !== 'terminated' && s !== 'cancelled') {
        activeEntityIds.add(eid);
      }
      const juris = (c.entity_jurisdiction || 'Other').trim();
      if (juris === 'Domestic') domesticEntityIds.add(eid);
      else if (juris === 'Foreign') foreignEntityIds.add(eid);
      else otherEntityIds.add(eid);
      
      // Entity type breakdown (by entity_id + type)
      const type = (c.entity_type || '(Unspecified)').trim();
      const key = `${juris} ${type}`;
      entityTypeMap.set(key, (entityTypeMap.get(key) || 0) + 1);
    }
    
    // Recount unique entities per category
    const domestic = list.filter(c => {
      const eid = (c.entity_id || String(c.id)).trim();
      return (c.entity_jurisdiction || '') === 'Domestic' && !domesticEntityIds.has(eid) ? false : false;
    }).length;
    
    // Count unique entity_ids per jurisdiction
    const domesticCount = new Set<string>();
    const foreignCount = new Set<string>();
    const otherCount = new Set<string>();
    const typeUniqueMap = new Map<string, Set<string>>();
    
    for (const c of list) {
      const eid = (c.entity_id || String(c.id)).trim();
      const juris = (c.entity_jurisdiction || 'Other').trim();
      const type = (c.entity_type || '(Unspecified)').trim();
      
      if (juris === 'Domestic') domesticCount.add(eid);
      else if (juris === 'Foreign') foreignCount.add(eid);
      else otherCount.add(eid);
      
      const typeKey = `${juris} ${type}`;
      if (!typeUniqueMap.has(typeKey)) typeUniqueMap.set(typeKey, new Set());
      typeUniqueMap.get(typeKey)!.add(eid);
    }
    
    const entityTypeBreakdown = Array.from(typeUniqueMap.entries()).map(([key, set]) => [key, set.size] as [string, number]).sort((a, b) => b[1] - a[1]);
    
    return { 
      total, 
      active: activeEntityIds.size, 
      domestic: domesticCount.size, 
      foreign: foreignCount.size, 
      other: otherCount.size, 
      entityTypeBreakdown 
    };
  };

  // === Active companies (excl. Terminated/Cancelled by default, matching list view) ===
  const activeCompanies = useMemo(() => {
    return companies.filter(c => {
      const s = (c.status || '').toLowerCase();
      return s !== 'terminated' && s !== 'cancelled';
    });
  }, [companies]);

  // === Report: Group by GROUP (use ALL companies from DB, including Terminated) ===
  const groupedByGroup = useMemo(() => {
    const map = new Map<string, Company[]>();
    for (const c of companies) {
      const key = (c.group_name || c.group || '(No Group)').trim() || '(No Group)';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    }
    return Array.from(map.entries()).sort((a, b) => {
      const sa = a[0] === '(No Group)' ? 2 : 1;
      const sb = b[0] === '(No Group)' ? 2 : 1;
      if (sa !== sb) return sa - sb;
      return a[0].localeCompare(b[0]);
    });
  }, [companies]);

  // === Report: Group by Jurisdiction (Domestic/Foreign/Other) ===
  const groupedByJurisdiction = useMemo(() => {
    const map = new Map<string, Company[]>();
    for (const c of companies) {
      const key = getTopCategory(c);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    }
    // Sort: Domestic first, then Foreign, then Other
    return Array.from(map.entries()).sort((a, b) => {
      const order: Record<string, number> = { Domestic: 0, Foreign: 1, Other: 2 };
      return (order[a[0]] ?? 99) - (order[b[0]] ?? 99);
    });
  }, [companies]);

  // === Report: Personnel Statistics ===
  const [expandedPerson, setExpandedPerson] = useState<number | null>(null);
  const [personnelStats, setPersonnelStats] = useState<Person[]>([]);
  const [personnelLoading, setPersonnelLoading] = useState(false);
  const [personSearchTerm, setPersonSearchTerm] = useState('');



  const ROLE_FILTER_OPTIONS = [
    { value: '', label: 'All Roles' },
    { value: 'CEO', label: 'CEO' },
    { value: 'MANAGER', label: 'MANAGER' },
    { value: 'CFO', label: 'CFO' },
    { value: 'TAX REP', label: 'TAX REP.' },
    { value: 'SECRETARY', label: 'SECRETARY' },
    { value: 'REGISTERED_AGENT', label: 'REGISTERED AGENT' },
    { value: 'DIRECTOR', label: 'DIRECTOR' },
    { value: 'OWNER/MEMBER', label: 'OWNER/MEMBER' },
    { value: 'UNASSIGNED', label: '未設定角色' },
  ];
  const ROLE_DISPLAY_LABEL: Record<string, string> = {
    'CEO': 'CEO',
    'MANAGER': 'MANAGER',
    'CFO': 'CFO',
    'TAX REP': 'TAX REP.',
    'TAX REP.': 'TAX REP.',
    'SECRETARY': 'SECRETARY',
    'REGISTERED_AGENT': 'REGISTERED AGENT',
    'DIRECTOR': 'DIRECTOR',
    'OWNER/MEMBER': 'OWNER/MEMBER',
    'MEMBER': 'MEMBER',
    'SHAREHOLDER': 'SHAREHOLDER',
    'UNASSIGNED': '未設定角色',
  };
  const getRoleDisplayLabel = (role: string): string => {
    if (!role) return '';
    if (ROLE_DISPLAY_LABEL[role]) return ROLE_DISPLAY_LABEL[role];
    const upper = role.trim().toUpperCase();
    if (ROLE_DISPLAY_LABEL[upper]) return ROLE_DISPLAY_LABEL[upper];
    const noPeriod = upper.replace(/\.+$/, '').trim();
    if (ROLE_DISPLAY_LABEL[noPeriod]) return ROLE_DISPLAY_LABEL[noPeriod];
    // fallback: if matches tax rep variant with any separators, return TAX REP.
    if (/^TAX[\s\-_.]*REP\.?$/i.test(role)) return 'TAX REP.';
    return role;
  };
  const LINKED_COMPANIES_FILTER_OPTIONS = [
    { value: '', label: 'All' },
    { value: '1', label: '1 Company' },
    { value: '2-10', label: '2-10 Companies' },
    { value: '10+', label: '10+ Companies' },
  ];
  const [personRoleFilter, setPersonRoleFilter] = useState<string>('');
  const [personLinkedCompaniesFilter, setPersonLinkedCompaniesFilter] = useState<string>('');
  const [personGroupFilter, setPersonGroupFilter] = useState<string>('');

  const fetchPersonnelStats = async (extraFilters?: Partial<PersonStatsFilters>) => {
    setPersonnelLoading(true);
    try {
      const filters: PersonStatsFilters = {
        ...(extraFilters || {}),
      };
      const res = await personApi.getStats(filters);
      setPersonnelStats(res.data || []);
    } catch (err) {
      console.error('Failed to fetch personnel stats:', err);
    } finally {
      setPersonnelLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'report') {
      fetchPersonnelStats({
        ...(personRoleFilter ? { role: personRoleFilter } : {}),
        ...(personLinkedCompaniesFilter ? { company_count_range: personLinkedCompaniesFilter } : {}),
        ...(personGroupFilter ? { company_group: personGroupFilter } : {}),
      });
    }
  }, [viewMode, personRoleFilter, personLinkedCompaniesFilter, personGroupFilter]);

  const availablePersonGroups = useMemo(() => {
    const set = new Set<string>();
    for (const c of companies) {
      const key = (c.group_name || c.group || '').trim() || '(No Group)';
      set.add(key);
    }
    return Array.from(set).sort((a, b) => {
      const sa = a === '(No Group)' ? 2 : 1;
      const sb = b === '(No Group)' ? 2 : 1;
      if (sa !== sb) return sa - sb;
      return a.localeCompare(b);
    });
  }, [companies]);

  const filteredPersonnelStats = useMemo(() => {
    let list = personnelStats;
    if (personSearchTerm.trim()) {
      const q = personSearchTerm.toLowerCase();
      list = personnelStats.filter(p =>
        (p.person_name?.toLowerCase().includes(q) ||
         (p.english_name && p.english_name.toLowerCase().includes(q)) ||
         (p.normalized_name && p.normalized_name.toLowerCase().includes(q)) ||
         (p.wecom_display_name && p.wecom_display_name.toLowerCase().includes(q)))
      );
    }
    return [...list].sort((a, b) => (b.company_count || 0) - (a.company_count || 0));
  }, [personnelStats, personSearchTerm]);

  // === Count unique entity_ids from a company list ===
  const countUniqueEntities = (list: Company[]): Set<string> => {
    const set = new Set<string>();
    for (const c of list) {
      set.add((c.entity_id || String(c.id)).trim());
    }
    return set;
  };

  // === Report: Chart data (use ALL companies from DB) ===
  const companyChartData = useMemo(() => {
    // Filter by entity scope
    let scopedCompanies = companies;
    if (entityScope.length > 0) {
      scopedCompanies = companies.filter(c => {
        const j = (c.entity_jurisdiction || 'Other').trim();
        return entityScope.includes(j);
      });
    }

    // Unique entity_id sets for each grouping
    const byGroup = groupedByGroup.map(([name, list]) => ({ 
      name, 
      value: countUniqueEntities(
        entityScope.length > 0
          ? list.filter(c => { const j = (c.entity_jurisdiction || 'Other').trim(); return entityScope.includes(j); })
          : list
      ).size
    }));
    
    const byJurisdiction = groupedByJurisdiction.map(([name, list]) => ({ 
      name, 
      value: countUniqueEntities(list).size 
    }));

    // By entity type (top category) - unique entity_ids
    const typeEntityMap = new Map<string, Set<string>>();
    for (const c of scopedCompanies) {
      const cat = getTopCategory(c);
      const eid = (c.entity_id || String(c.id)).trim();
      if (!typeEntityMap.has(cat)) typeEntityMap.set(cat, new Set());
      typeEntityMap.get(cat)!.add(eid);
    }
    const entityTypeArr = Array.from(typeEntityMap.entries()).map(([name, set]) => ({ name, value: set.size }));

    // By entity type field - unique entity_ids
    const typeFieldMap = new Map<string, Set<string>>();
    for (const c of scopedCompanies) {
      const t = (c.entity_type || '(Unspecified)').trim();
      const eid = (c.entity_id || String(c.id)).trim();
      if (!typeFieldMap.has(t)) typeFieldMap.set(t, new Set());
      typeFieldMap.get(t)!.add(eid);
    }
    const entityTypeFieldArr = Array.from(typeFieldMap.entries()).sort((a, b) => b[1].size - a[1].size).map(([name, set]) => ({ name, value: set.size }));

    // By state - unique entity_ids
    const stateEntityMap = new Map<string, Set<string>>();
    for (const c of scopedCompanies) {
      const s = (c.state || '(Unknown)').trim();
      const eid = (c.entity_id || String(c.id)).trim();
      if (!stateEntityMap.has(s)) stateEntityMap.set(s, new Set());
      stateEntityMap.get(s)!.add(eid);
    }
    const stateArr = Array.from(stateEntityMap.entries()).sort((a, b) => b[1].size - a[1].size).slice(0, 10).map(([name, set]) => ({ name, value: set.size }));

    // By status - unique entity_ids
    const statusEntityMap = new Map<string, Set<string>>();
    for (const c of scopedCompanies) {
      const s = (c.status || 'Unknown').trim();
      const eid = (c.entity_id || String(c.id)).trim();
      if (!statusEntityMap.has(s)) statusEntityMap.set(s, new Set());
      statusEntityMap.get(s)!.add(eid);
    }
    const statusArr = Array.from(statusEntityMap.entries()).map(([name, set]) => ({ name, value: set.size }));

    // Entity Classification Analysis
    // Classification | LLC | INC | LIMITED | Total
    const classificationMap = new Map<string, { LLC: Set<string>; INC: Set<string>; LIMITED: Set<string> }>();
    const initCls = () => ({ LLC: new Set<string>(), INC: new Set<string>(), LIMITED: new Set<string>() });
    
    const jurisList = ['Domestic', 'Foreign', 'Other'];
    for (const juris of jurisList) {
      classificationMap.set(juris, initCls());
    }

    for (const c of scopedCompanies) {
      const juris = (c.entity_jurisdiction || 'Other').trim();
      const type = (c.entity_type || '').trim().toUpperCase();
      const eid = (c.entity_id || String(c.id)).trim();
      const clsEntry = classificationMap.get(juris) || initCls();
      
      if (type === 'LLC' || type === 'LP') {
        clsEntry.LLC.add(eid);
      } else if (type === 'INC' || type === 'CORPORATION' || type === 'CORP' || type === 'S-LIMITED BY SHARES' || type === 'S-CORPORATION') {
        clsEntry.INC.add(eid);
      } else if (type === 'LIMITED' || type === 'S-LIMITED') {
        clsEntry.LIMITED.add(eid);
      }
      classificationMap.set(juris, clsEntry);
    }

    const classification = Array.from(classificationMap.entries()).map(([cls, sets]) => ({
      classification: cls,
      LLC: sets.LLC.size,
      INC: sets.INC.size,
      LIMITED: sets.LIMITED.size,
      Total: sets.LLC.size + sets.INC.size + sets.LIMITED.size,
    }));
    const clsOrder = { Domestic: 0, Foreign: 1, Other: 2 };
    classification.sort((a, b) => (clsOrder[a.classification] ?? 99) - (clsOrder[b.classification] ?? 99));

    return { 
      byGroup, 
      byJurisdiction, 
      byEntityType: entityTypeArr, 
      byEntityTypeField: entityTypeFieldArr, 
      byState: stateArr, 
      byStatus: statusArr,
      classification,
      uniqueTotal: countUniqueEntities(scopedCompanies).size
    };
  }, [groupedByGroup, groupedByJurisdiction, companies, entityScope]);

  // === Merged Report: Group × Classification × Entity Type ===
  type ClassData = {
    companies: Company[];
    uniqueEids: Set<string>;
    LLC: Set<string>;
    INC: Set<string>;
    LIMITED: Set<string>;
  };
  type GroupReport = {
    groupName: string;
    Domestic: ClassData;
    Foreign: ClassData;
    Other: ClassData;
  };

  const initClassData = (): ClassData => ({
    companies: [],
    uniqueEids: new Set(),
    LLC: new Set(),
    INC: new Set(),
    LIMITED: new Set(),
  });

  const mergedReport = useMemo(() => {
    const jurisList = ['Domestic', 'Foreign', 'Other'];
    const result: GroupReport[] = [];

    for (const [groupName, groupCompanies] of groupedByGroup) {
      const scoped = entityScope.length > 0
        ? groupCompanies.filter(c => entityScope.includes((c.entity_jurisdiction || 'Other').trim()))
        : groupCompanies;

      const report: GroupReport = {
        groupName,
        Domestic: initClassData(),
        Foreign: initClassData(),
        Other: initClassData(),
      };

      for (const c of scoped) {
        const juris = (c.entity_jurisdiction || 'Other').trim();
        const type = (c.entity_type || '').trim().toUpperCase();
        const eid = (c.entity_id || String(c.id)).trim();
        const target = (report[juris] || report.Other);

        target.companies.push(c);
        target.uniqueEids.add(eid);

        if (type === 'LLC' || type === 'LP') target.LLC.add(eid);
        else if (type === 'INC' || type === 'CORPORATION' || type === 'CORP' || type === 'S-LIMITED BY SHARES' || type === 'S-CORPORATION') target.INC.add(eid);
        else if (type === 'LIMITED' || type === 'S-LIMITED') target.LIMITED.add(eid);
      }

      result.push(report);
    }

    return result;
  }, [groupedByGroup, companies, entityScope]);

  // Helper: render stats summary bar for a group
  const renderStatsBar = (list: Company[]) => {
    const s = getGroupStats(list);
    return (
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3 px-4 py-3 bg-white border-b border-slate-200">
        <div>
          <div className="text-xs text-slate-500">Total</div>
          <div className="text-lg font-bold text-slate-800">{s.total}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Active</div>
          <div className="text-lg font-bold text-green-700">{s.active}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Domestic</div>
          <div className="text-lg font-bold text-blue-700">{s.domestic}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Foreign</div>
          <div className="text-lg font-bold text-purple-700">{s.foreign}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Other</div>
          <div className="text-lg font-bold text-amber-700">{s.other}</div>
        </div>
        <div className="col-span-2">
          <div className="text-xs text-slate-500 mb-1">Entity Type</div>
          <div className="flex flex-wrap gap-1">
            {s.entityTypeBreakdown.map(([type, count]) => (
              <span key={type} className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-full">
                {type}: {count}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-6">
        {showTrash ? (
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowTrash(false)}
              className="flex items-center space-x-2 px-3 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to List</span>
            </button>
            <h1 className="text-2xl font-bold text-orange-600">Trash</h1>
          </div>
        ) : (
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{t('nav.companyManagement')}</h1>
            <p className="text-slate-500 mt-1">{t('company.manageDesc')}</p>
          </div>
        )}
        <div className="flex items-center space-x-3">
          <button
            onClick={() => {
              saveState();
              setFromPage();
              const currentPath = location.pathname + location.search;
              navigate('/company/new', { state: { from: currentPath } });
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>{t('company.addNew')}</span>
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            <span>{exporting ? t('msg.loading') : t('action.export')}</span>
          </button>
          <label className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg cursor-pointer">
            <Upload className="w-4 h-4" />
            <span>{importing ? t('msg.loading') : t('action.import')}</span>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImport}
              disabled={importing}
              className="hidden"
            />
          </label>
          <button
            onClick={() => {
              setShowTrash(true);
              fetchDeletedCompanies();
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            <span>Trash</span>
          </button>
        </div>
      </div>

      <ViewToggle
        viewMode={viewMode}
        onChange={setViewMode}
        show={!showTrash}
      />

      {/* === REPORT VIEW === */}
      {viewMode === 'report' && !showTrash && (
        <div className="space-y-4">

          {/* Group Entity Distribution */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-slate-700">Group Entity Distribution</h2>
                  <p className="text-xs text-slate-400 mt-0.5">
                    All scopes · Based on Legal Entities
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <span className="text-xs text-slate-500">Total Registrations: </span>
                    <span className="font-bold text-slate-800">{companyChartData.uniqueTotal || 0}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-4 py-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
                      <th className="text-left px-3 py-2 font-semibold w-8"></th>
                      <th className="text-left px-3 py-2 font-semibold">Group</th>
                      <th className="text-center px-3 py-2 font-semibold">Domestic</th>
                      <th className="text-center px-3 py-2 font-semibold">Foreign</th>
                      <th className="text-center px-3 py-2 font-semibold">Other</th>
                      <th className="text-center px-3 py-2 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {mergedReport.map((row) => {
                      const domCount = row.Domestic.uniqueEids.size;
                      const forCount = row.Foreign.uniqueEids.size;
                      const othCount = row.Other.uniqueEids.size;
                      const totalCount = domCount + forCount + othCount;
                      const isExpanded = mergedExpandedGroup === row.groupName;

                      if (totalCount === 0) {
                        return (
                          <tr key={row.groupName} className="hover:bg-slate-50 transition-colors">
                            <td className="px-3 py-2.5"></td>
                            <td className="px-3 py-2.5 font-medium text-slate-700">{row.groupName}</td>
                            <td className="px-3 py-2.5 text-center text-slate-400">{domCount || '-'}</td>
                            <td className="px-3 py-2.5 text-center text-slate-400">{forCount || '-'}</td>
                            <td className="px-3 py-2.5 text-center text-slate-400">{othCount || '-'}</td>
                            <td className="px-3 py-2.5 text-center font-semibold text-slate-500">{totalCount}</td>
                          </tr>
                        );
                      }

                      const classifications = [
                        { key: 'Domestic', data: row.Domestic },
                        { key: 'Foreign', data: row.Foreign },
                        { key: 'Other', data: row.Other },
                      ];

                      return (
                        <Fragment key={row.groupName}>
                          <tr
                            className="hover:bg-slate-50 transition-colors cursor-pointer"
                            onClick={() => {
                              setMergedExpandedGroup(isExpanded ? null : row.groupName);
                              setMergedExpandedClass(null);
                              setMergedExpandedSub(null);
                            }}
                          >
                            <td className="px-3 py-2.5">
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                            </td>
                            <td className="px-3 py-2.5 font-medium text-slate-700">{row.groupName}</td>
                            <td className="px-3 py-2.5 text-center text-slate-600">{domCount}</td>
                            <td className="px-3 py-2.5 text-center text-slate-600">{forCount}</td>
                            <td className="px-3 py-2.5 text-center text-slate-600">{othCount}</td>
                            <td className="px-3 py-2.5 text-center font-semibold text-slate-800">{totalCount}</td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-slate-50/60">
                              <td colSpan={6} className="px-6 py-3">
                                <div className="space-y-2">
                                  {classifications.map(({ key: clsKey, data }) => {
                                    const clsTotal = data.uniqueEids.size;
                                    if (clsTotal === 0) return null;
                                    const clsKey2 = `${row.groupName}|${clsKey}`;
                                    const clsExpanded = mergedExpandedClass === clsKey2;
                                    const activeEids = new Set<string>();
                                    for (const c of data.companies) {
                                      if ((c.status || '').toLowerCase() === 'active') {
                                        activeEids.add((c.entity_id || String(c.id)).trim());
                                      }
                                    }

                                    return (
                                      <div key={clsKey} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setMergedExpandedClass(clsExpanded ? null : clsKey2);
                                            setMergedExpandedSub(null);
                                          }}
                                          className="w-full px-3 py-2 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                                        >
                                          <div className="flex items-center gap-2">
                                            {clsExpanded ? (
                                              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                                            ) : (
                                              <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                                            )}
                                            <span className="text-sm text-slate-700 font-medium">{clsKey}</span>
                                          </div>
                                          <div className="flex items-center gap-3 text-xs text-slate-500">
                                            <span>Entities <span className="font-semibold text-slate-700">{clsTotal}</span></span>
                                            <span>Active <span className="font-semibold text-green-700">{activeEids.size}</span></span>
                                            <span className="text-slate-400">LLC <b>{data.LLC.size}</b> · INC <b>{data.INC.size}</b> · LIMITED <b>{data.LIMITED.size}</b></span>
                                          </div>
                                        </button>
                                        {clsExpanded && (
                                          <div className="px-3 pb-3">
                                            {/* Sub-type breakdown - flat list */}
                                            <div className="divide-y divide-slate-100">
                                              {(['LLC', 'INC', 'LIMITED'] as const).map((subKey) => {
                                                const subSet = data[subKey];
                                                const subCount = subSet.size;
                                                const subLabel = subKey === 'LLC' ? 'LLC' : subKey === 'INC' ? 'INC' : 'LIMITED';
                                                const fullSubKey = `${row.groupName}|${clsKey}|${subKey}`;
                                                const subExpanded = mergedExpandedSub === fullSubKey;
                                                const subCompanies = data.companies.filter(c => {
                                                  const type = (c.entity_type || '').trim().toUpperCase();
                                                  const eid = (c.entity_id || String(c.id)).trim();
                                                  if (subKey === 'LLC') return (type === 'LLC' || type === 'LP') && subSet.has(eid);
                                                  if (subKey === 'INC') return (type === 'INC' || type === 'CORPORATION' || type === 'CORP' || type === 'S-LIMITED BY SHARES' || type === 'S-CORPORATION') && subSet.has(eid);
                                                  if (subKey === 'LIMITED') return (type === 'LIMITED' || type === 'S-LIMITED') && subSet.has(eid);
                                                  return false;
                                                });

                                                if (subCount === 0) {
                                                  return (
                                                    <div key={subKey} className="py-1.5 flex items-center justify-between text-xs text-slate-400">
                                                      <span className="font-medium">{subLabel}</span>
                                                      <span className="font-bold">0</span>
                                                    </div>
                                                  );
                                                }

                                                return (
                                                  <div key={subKey}>
                                                    <button
                                                      type="button"
                                                      onClick={() => setMergedExpandedSub(subExpanded ? null : fullSubKey)}
                                                      className="w-full py-1.5 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                                                    >
                                                      <div className="flex items-center gap-1.5">
                                                        {subExpanded ? (
                                                          <ChevronDown className="w-3 h-3 text-slate-400" />
                                                        ) : (
                                                          <ChevronRight className="w-3 h-3 text-slate-400" />
                                                        )}
                                                        <span className="text-xs font-semibold text-slate-700">{subLabel}</span>
                                                      </div>
                                                      <span className="text-sm font-bold text-slate-600">{subCount}</span>
                                                    </button>
                                                    {subExpanded && (
                                                      <div className="pb-2">
                                                        <div className="overflow-x-auto">
                                                          <table className="w-full text-xs">
                                                            <thead>
                                                              <tr className="text-slate-500 uppercase tracking-wider border-b border-slate-100">
                                                                <th className="text-left px-2 py-1 font-medium">Nickname</th>
                                                                <th className="text-left px-2 py-1 font-medium">Company Name</th>
                                                                <th className="text-center px-2 py-1 font-medium">State</th>
                                                                <th className="text-center px-2 py-1 font-medium">Status</th>
                                                                <th className="text-center px-2 py-1 font-medium">Actions</th>
                                                              </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-slate-100">
                                                              {subCompanies
                                                                .slice()
                                                                .sort((a, b) => {
                                                                  const sOrder = (s) => { const u = (s || '').toUpperCase(); if (u === 'ACTIVE') return 0; if (u === 'TERMINATED') return 2; return 1; };
                                                                  const d = sOrder(a.status) - sOrder(b.status);
                                                                  return d !== 0 ? d : (a.company_name || '').localeCompare(b.company_name || '');
                                                                })
                                                                .map((company) => (
                                                                  <tr key={company.id} className="hover:bg-slate-50">
                                                                    <td className="px-2 py-1 text-slate-600 whitespace-nowrap">{company.nickname || '-'}</td>
                                                                    <td className="px-2 py-1 text-slate-800 font-medium whitespace-nowrap max-w-[180px] truncate" title={company.company_name}>{company.company_name || '-'}</td>
                                                                    <td className="px-2 py-1 text-slate-600 text-center whitespace-nowrap">{company.state || '-'}</td>
                                                                    <td className="px-2 py-1 text-center whitespace-nowrap">
                                                                      <span className={`px-1.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${getStageColor(company.status || '')}`}>
                                                                        {company.status || '-'}
                                                                      </span>
                                                                    </td>
                                                                    <td className="px-2 py-1 text-center">
                                                                      <button
                                                                        onClick={(e) => { e.stopPropagation(); setViewingCompany(company); }}
                                                                        className="p-0.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors inline-flex items-center"
                                                                        title="View Details"
                                                                      >
                                                                        <Eye className="w-3.5 h-3.5" />
                                                                      </button>
                                                                    </td>
                                                                  </tr>
                                                                ))}
                                                            </tbody>
                                                          </table>
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                    {mergedReport.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-slate-400">No data available</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-slate-50">
                    <tr className="text-xs font-semibold text-slate-700">
                      <td className="px-3 py-2.5"></td>
                      <td className="px-3 py-2.5">Total</td>
                      <td className="px-3 py-2.5 text-center">
                        {mergedReport.reduce((sum, r) => sum + r.Domestic.uniqueEids.size, 0)}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {mergedReport.reduce((sum, r) => sum + r.Foreign.uniqueEids.size, 0)}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {mergedReport.reduce((sum, r) => sum + r.Other.uniqueEids.size, 0)}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {mergedReport.reduce((sum, r) => sum + r.Domestic.uniqueEids.size + r.Foreign.uniqueEids.size + r.Other.uniqueEids.size, 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Charts Row - State & Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SimpleBarChart
              data={companyChartData.byState}
              title="Top States by Entity Count"
            />
            <SimplePieChart
              data={companyChartData.byStatus}
              title="Status Distribution"
            />
          </div>

          {/* Personnel Statistics */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="px-3 py-2 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-slate-700">Personnel Statistics</h2>
                <span className="text-xs text-slate-400">{personnelStats.length} people</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    title="Search by Full Name or Nickname"
                    value={personSearchTerm}
                    onChange={(e) => setPersonSearchTerm(e.target.value)}
                    className="w-40 pl-7 pr-2 py-1 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={personRoleFilter}
                  onChange={(e) => setPersonRoleFilter(e.target.value)}
                  title="Role Filter"
                  className="px-2 py-1 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {ROLE_FILTER_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  value={personLinkedCompaniesFilter}
                  onChange={(e) => setPersonLinkedCompaniesFilter(e.target.value)}
                  title="Linked Companies Filter"
                  className="px-2 py-1 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {LINKED_COMPANIES_FILTER_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  value={personGroupFilter}
                  onChange={(e) => setPersonGroupFilter(e.target.value)}
                  title="Group Filter"
                  className="px-2 py-1 text-xs bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Groups</option>
                  {availablePersonGroups.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center px-3 py-1.5 bg-slate-50/80 border-b border-slate-100 text-xs font-medium text-slate-500 uppercase tracking-wider">
              <div className="w-5 flex-shrink-0"></div>
              <div className="w-6 flex-shrink-0"></div>
              <div className="flex-[2] min-w-0">Full Name</div>
              <div className="flex-[2] min-w-0">Nickname</div>
              <div className="w-40 flex-shrink-0 text-center">Linked Companies</div>
            </div>
            <div className="divide-y divide-slate-100">
              {personnelLoading ? (
                <div className="px-4 py-8 text-center text-slate-400 text-sm">Loading...</div>
              ) : filteredPersonnelStats.length === 0 ? (
                <div className="px-4 py-8 text-center text-slate-400 text-sm">No personnel data</div>
              ) : (
                filteredPersonnelStats.map((person) => {
                  const isExpanded = expandedPerson === person.id;
                  const displayName = person.display_name || person.person_name || person.normalized_name;
                  const firstChar = (displayName.charAt(0) || '?').toUpperCase();
                  const linkedCount = person.company_count || 0;
                  return (
                    <Fragment key={person.id}>
                      <div
                        className="px-3 py-1.5 cursor-pointer hover:bg-slate-50/50 transition-colors flex items-center text-sm"
                        onClick={() => setExpandedPerson(isExpanded ? null : person.id!)}
                      >
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          {isExpanded ? (
                            <ChevronDown className="w-3 h-3 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-3 h-3 text-slate-400" />
                          )}
                        </div>
                        <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-shrink-0">
                          {firstChar}
                        </div>
                        <div className="flex-[2] min-w-0 px-2">
                          <span className="text-slate-700 font-medium truncate block">{displayName}</span>
                        </div>
                        <div className="flex-[2] min-w-0 px-2">
                          <span className="text-slate-500 truncate block">{person.english_name || '-'}</span>
                        </div>
                        <div className="w-40 flex-shrink-0 text-center tabular-nums text-slate-600 text-sm font-medium">
                          {linkedCount}
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="bg-slate-50/60 px-3 py-2 pl-11">
                          {person.role_companies && Object.keys(person.role_companies).length > 0 ? (
                            <div className="space-y-2">
                              {Object.entries(person.role_companies).map(([role, companies]) => (
                                <div key={role}>
                                  <div className="text-xs font-medium text-slate-600 mb-1">
                                    <span>{getRoleDisplayLabel(role)} ({Array.isArray(companies) ? companies.length : 0})</span>
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {companies.map((co: any) => {
                                      const name = typeof co === 'string' ? co : (co.company_name || co.nickname || '-');
                                      return (
                                        <span
                                          key={name}
                                          className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-600"
                                        >
                                          {name}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : person.companies && person.companies.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {person.companies.map((name: string) => (
                                <span
                                  key={name}
                                  className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-600"
                                >
                                  {name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div className="text-xs text-slate-400">No linked companies</div>
                          )}
                        </div>
                      )}
                    </Fragment>
                  );
                })
              )}
            </div>
          </div>


        </div>
      )}

      {/* === LIST VIEW === */}
      {viewMode === 'list' && (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('company.searchPlaceholder')}
                className="pl-10 pr-4 py-2 w-full bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {showTrash ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse table-fixed">
              <colgroup>
                <col className="w-[8%]" />
                <col className="w-[12%]" />
                <col className="w-[30%]" />
                <col className="w-[12%]" />
                <col className="w-[22%]" />
                <col className="w-[16%]" />
              </colgroup>
              <thead className="bg-orange-50">
                <tr>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-orange-600 uppercase tracking-wider border-b border-orange-200">State</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-orange-600 uppercase tracking-wider border-b border-orange-200">Nickname</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-orange-600 uppercase tracking-wider border-b border-orange-200">Company Name</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-orange-600 uppercase tracking-wider border-b border-orange-200">Deleted Date</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-orange-600 uppercase tracking-wider border-b border-orange-200">Reason</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-orange-600 uppercase tracking-wider border-b border-orange-200">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-orange-100">
                {deletedCompanies.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-10 text-center text-slate-500">
                      Trash is empty
                    </td>
                  </tr>
                ) : (
                  deletedCompanies.map((company) => (
                    <tr key={company.id} className="hover:bg-orange-50 transition-colors">
                      <td className="px-3 py-2.5 text-sm text-slate-700 text-center">{company.state || '-'}</td>
                      <td className="px-3 py-2.5 text-sm text-slate-700 text-center">{company.nickname || '-'}</td>
                      <td className="px-3 py-2.5 text-sm text-slate-700 text-center truncate" title={company.company_name}>{company.company_name}</td>
                      <td className="px-3 py-2.5 text-sm text-slate-700 text-center">
                        {company.deleted_at ? new Date(company.deleted_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-slate-700 text-center truncate" title={company.deleted_reason}>{company.deleted_reason || '-'}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => handleRestore(company.id)}
                            className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Restore"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(company.id)}
                            className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Permanently Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-visible">
            <table className="w-full border-collapse table-fixed">
              <colgroup>
                <col className="w-[6%]" />
                <col className="w-[11%]" />
                <col className="w-[8%]" />
                <col className="w-[14%]" />
                <col className="w-[8%]" />
                <col className="w-[28%]" />
                <col className="w-[9%]" />
                <col className="w-[16%]" />
              </colgroup>
              <thead className="bg-slate-50">
                <tr>
                  {([
                    { key: 'state', label: 'State', values: uniqueStates },
                    { key: 'entity_jurisdiction', label: 'Entity Jurisdiction', values: uniqueEntityJurisdictions },
                    { key: 'entity_type', label: 'Entity Type', values: uniqueEntityTypes },
                    { key: 'category', label: 'Category', values: uniqueCategories },
                  ] as const).map((col, idx, arr) => {
                    const alignClass = idx === 0 ? 'left-0' : idx === arr.length - 1 ? 'right-0' : 'left-1/2 -translate-x-1/2';
                    return (
                    <th key={col.key} className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                      <div className="flex items-center justify-center gap-1 relative">
                        <span>{col.label}</span>
                        <button
                          data-filter-popup
                          onClick={() => setActiveFilterCol(activeFilterCol === col.key ? null : col.key)}
                          className={`p-0.5 rounded transition-colors ${columnFilters[col.key].length > 0 ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                          title={`Filter by ${col.label}`}
                        >
                          <Filter className="w-3 h-3" />
                        </button>
                        {activeFilterCol === col.key && (
                          <div data-filter-popup className={`absolute top-full ${alignClass} mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1`}>
                            <div className="px-2 pb-1">
                              <input
                                type="text"
                                placeholder="Search..."
                                value={filterSearch[col.key] || ''}
                                onChange={(e) => setFilterSearch(fs => ({ ...fs, [col.key]: e.target.value }))}
                                onClick={(e) => e.stopPropagation()}
                                className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-300"
                                autoFocus
                              />
                            </div>
                            <button
                              onClick={() => { setColumnFilters(f => ({ ...f, [col.key]: [] })); setFilterSearch(fs => ({ ...fs, [col.key]: '' })); setActiveFilterCol(null); }}
                              className="w-full flex items-center justify-between px-3 py-1 text-xs text-slate-500 hover:bg-slate-50 whitespace-nowrap"
                            >
                              <span>Clear</span>
                              {columnFilters[col.key].length > 0 && (
                                <span className="text-slate-400 tabular-nums">{columnFilters[col.key].length} selected</span>
                              )}
                            </button>
                            <div className="border-t border-slate-100 my-1"></div>
                            <div className="max-h-48 overflow-y-auto">
                              {(() => {
                                const filtered = col.values.filter(v => !filterSearch[col.key] || v.toLowerCase().includes(filterSearch[col.key].toLowerCase()));
                                if (filtered.length === 0) return <div className="px-3 py-2 text-xs text-slate-400">No matches</div>;
                                return filtered.map(v => {
                                const checked = columnFilters[col.key].includes(v);
                                const cnt = getColumnCount(col.key, v);
                                return (
                                  <label
                                    key={v}
                                    className="flex items-start gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => {
                                        setColumnFilters(f => ({
                                          ...f,
                                          [col.key]: checked
                                            ? f[col.key].filter(x => x !== v)
                                            : [...f[col.key], v]
                                        }));
                                      }}
                                      className="w-3 h-3 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                                    />
                                    <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                                      <span className={`min-w-0 text-left break-words ${checked ? 'text-blue-600 font-medium' : 'text-slate-700'}`}>{v}</span>
                                      <span className="text-slate-400 tabular-nums flex-shrink-0">({cnt})</span>
                                    </div>
                                  </label>
                                );
                              });
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </th>
                    );
                  })}
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                    <div className="flex items-center justify-center gap-1 relative">
                      <span>Nickname</span>
                      <button
                        data-filter-popup
                        onClick={() => setActiveFilterCol(activeFilterCol === 'nickname' ? null : 'nickname')}
                        className={`p-0.5 rounded transition-colors ${columnFilters.nickname.length > 0 ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                        title="Filter by Nickname"
                      >
                        <Filter className="w-3 h-3" />
                      </button>
                      {activeFilterCol === 'nickname' && (
                        <div data-filter-popup className="absolute top-full left-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1">
                          <div className="px-2 pb-1">
                            <input
                              type="text"
                              placeholder="Search..."
                              value={filterSearch['nickname'] || ''}
                              onChange={(e) => setFilterSearch(fs => ({ ...fs, nickname: e.target.value }))}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-300"
                              autoFocus
                            />
                          </div>
                          <button
                            onClick={() => { setColumnFilters(f => ({ ...f, nickname: [] })); setFilterSearch(fs => ({ ...fs, nickname: '' })); setActiveFilterCol(null); }}
                            className="w-full flex items-center justify-between px-3 py-1 text-xs text-slate-500 hover:bg-slate-50 whitespace-nowrap"
                          >
                            <span>Clear</span>
                            {columnFilters.nickname.length > 0 && (
                              <span className="text-slate-400 tabular-nums">{columnFilters.nickname.length} selected</span>
                            )}
                          </button>
                          <div className="border-t border-slate-100 my-1"></div>
                          <div className="max-h-48 overflow-y-auto">
                            {(() => {
                              const filtered = uniqueNicknames.filter(v => !filterSearch['nickname'] || v.toLowerCase().includes(filterSearch['nickname'].toLowerCase()));
                              if (filtered.length === 0) return <div className="px-3 py-2 text-xs text-slate-400">No matches</div>;
                              return filtered.map(v => {
                              const checked = columnFilters.nickname.includes(v);
                              const cnt = getColumnCount('nickname', v);
                              return (
                                <label
                                  key={v}
                                  className="flex items-start gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50"
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                      setColumnFilters(f => ({
                                        ...f,
                                        nickname: checked
                                          ? f.nickname.filter(x => x !== v)
                                          : [...f.nickname, v]
                                      }));
                                    }}
                                    className="w-3 h-3 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                                  />
                                  <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                                    <span className={`min-w-0 text-left break-words ${checked ? 'text-blue-600 font-medium' : 'text-slate-700'}`}>{v}</span>
                                    <span className="text-slate-400 tabular-nums flex-shrink-0">({cnt})</span>
                                  </div>
                                </label>
                              );
                            });
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">Company Name</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                    <div className="flex items-center justify-center gap-1 relative">
                      <span>Status</span>
                      <button
                        data-filter-popup
                        onClick={() => setActiveFilterCol(activeFilterCol === 'status' ? null : 'status')}
                        className={`p-0.5 rounded transition-colors ${columnFilters.status.length > 0 ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                        title="Filter by Status"
                      >
                        <Filter className="w-3 h-3" />
                      </button>
                      {activeFilterCol === 'status' && (
                        <div data-filter-popup className="absolute top-full right-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1">
                          <div className="px-2 pb-1">
                            <input
                              type="text"
                              placeholder="Search..."
                              value={filterSearch['status'] || ''}
                              onChange={(e) => setFilterSearch(fs => ({ ...fs, status: e.target.value }))}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-300"
                              autoFocus
                            />
                          </div>
                          <button
                            onClick={() => { setColumnFilters(f => ({ ...f, status: [] })); setFilterSearch(fs => ({ ...fs, status: '' })); setActiveFilterCol(null); }}
                            className="w-full flex items-center justify-between px-3 py-1 text-xs text-slate-500 hover:bg-slate-50 whitespace-nowrap"
                          >
                            <span>Clear</span>
                            {columnFilters.status.length > 0 && (
                              <span className="text-slate-400 tabular-nums">{columnFilters.status.length} selected</span>
                            )}
                          </button>
                          <div className="border-t border-slate-100 my-1"></div>
                          <div className="max-h-48 overflow-y-auto">
                            {(() => {
                              const filtered = uniqueStatusValues.filter(v => !filterSearch['status'] || v.toLowerCase().includes(filterSearch['status'].toLowerCase()));
                              if (filtered.length === 0) return <div className="px-3 py-2 text-xs text-slate-400">No matches</div>;
                              return filtered.map(v => {
                              const checked = columnFilters.status.includes(v);
                              const cnt = getColumnCount('status', v);
                              return (
                                <label
                                  key={v}
                                  className="flex items-start gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50"
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                      setColumnFilters(f => ({
                                        ...f,
                                        status: checked
                                          ? f.status.filter(x => x !== v)
                                          : [...f.status, v]
                                      }));
                                    }}
                                    className="w-3 h-3 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                                  />
                                  <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                                    <span className={`min-w-0 text-left break-words ${checked ? 'text-blue-600 font-medium' : 'text-slate-700'}`}>{v}</span>
                                    <span className="text-slate-400 tabular-nums flex-shrink-0">({cnt})</span>
                                  </div>
                                </label>
                              );
                            });
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                      {t('company.loading')}
                    </td>
                  </tr>
                ) : filteredCompanies.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                      {t('company.noData')}
                    </td>
                  </tr>
                ) : (
                  paginatedCompanies.map((company) => (
                    <tr key={company.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-3 py-2.5 text-slate-600 text-sm text-center whitespace-nowrap">{company.state || '-'}</td>
                      <td className="px-3 py-2.5 text-slate-600 text-sm text-center whitespace-nowrap">{company.entity_jurisdiction || '-'}</td>
                      <td className="px-3 py-2.5 text-slate-600 text-sm text-center whitespace-nowrap">{company.entity_type || '-'}</td>
                      <td className="px-3 py-2.5 text-slate-600 text-sm text-center whitespace-nowrap truncate" title={company.category}>{company.category || '-'}</td>
                      <td className="px-3 py-2.5 text-slate-600 text-sm text-center whitespace-nowrap">{company.nickname || '-'}</td>
                      <td className="px-3 py-2.5 text-slate-800 text-sm font-medium text-center whitespace-nowrap truncate" title={company.company_name}>{company.company_name || '-'}</td>
                      <td className="px-3 py-2.5 text-center whitespace-nowrap">
                        <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${getStageColor(company.status || '')}`}>
                          {company.status || '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-center space-x-1">
                          <button
                            onClick={() => setViewingCompany(company)}
                            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              saveState();
                              setFromPage();
                              const currentPath = location.pathname + location.search;
                              navigate(`/company/edit/${company.id}`, { state: { from: currentPath } });
                            }}
                            className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                            title={t('action.edit')}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(company.id)}
                            className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title={t('action.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 分页控制 */}
        {filteredCompanies.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-600">{t('pagination.show')}</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-2 py-1 border border-slate-300 rounded text-sm"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={75}>75</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-slate-600">{t('pagination.perPage')}</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-600">
                {t('pagination.showing')} {startIndex + 1}-{Math.min(endIndex, filteredCompanies.length)} {t('pagination.of')} {companies.length} {t('pagination.records')}
                {filteredCompanies.length < companies.length && (
                  <span className="text-slate-400 ml-1">({filteredCompanies.length} visible)</span>
                )}
              </span>
            </div>

            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 text-sm border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('pagination.first')}
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('pagination.prev')}
              </button>
              
              {/* 页码显示 */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm border rounded ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-slate-300 hover:bg-slate-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('pagination.next')}
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-sm border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('pagination.last')}
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {viewingCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 p-4 border-b border-slate-200 flex justify-between items-center bg-white z-10">
              <h2 className="text-xl font-bold text-slate-800">{t('company.details')}</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    saveStateRef.current(); setFromPageRef.current(); navigate(`/company/edit/${viewingCompany.id}`, { state: { from: location.pathname + location.search } });
                  }}
                  className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => setViewingCompany(null)}
                  className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100 transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 border-b pb-2">Basic Information</h3>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Company Name</label>
                    <p className="text-slate-800">{viewingCompany.company_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Nickname</label>
                    <p className="text-slate-800">{viewingCompany.nickname || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">State</label>
                    <p className="text-slate-800">{viewingCompany.state || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Entity Jurisdiction</label>
                    <p className="text-slate-800">{viewingCompany.entity_jurisdiction || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Entity Type</label>
                    <p className="text-slate-800">{viewingCompany.entity_type || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Category</label>
                    <p className="text-slate-800">{viewingCompany.category || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Group</label>
                    <p className="text-slate-800">{viewingCompany.group_name || viewingCompany.group || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Status</label>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStageColor(viewingCompany.status || '')}`}>
                      {viewingCompany.status || '-'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Registration Date</label>
                    <p className="text-slate-800">{viewingCompany.registration_date || '-'}</p>
                  </div>
                  {viewingCompany.status === 'Terminated' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-500">Termination Date</label>
                      <p className="text-slate-800">{viewingCompany.termination_date || '-'}</p>
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 border-b pb-2">Tax & Confidential Information</h3>
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <label className="block text-sm font-medium text-yellow-700">Federal Tax ID (EIN)</label>
                    <p className="text-yellow-800 font-mono">{viewingCompany.federal_tax_id || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">State Tax ID</label>
                    <p className="text-slate-800">{viewingCompany.state_tax_id || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">SOS ID</label>
                    <p className="text-slate-800">{viewingCompany.sos_id || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">BOIR ID (BOI)</label>
                    <p className="text-slate-800">{viewingCompany.boir_id || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">DUNS</label>
                    <p className="text-slate-800">{viewingCompany.duns || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Principal Address</label>
                    <p className="text-slate-800">{viewingCompany.principal_address || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Mailing Address</label>
                    <p className="text-slate-800">{viewingCompany.mailing_address || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">State Office Address</label>
                    <p className="text-slate-800">{viewingCompany.state_office_address || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Registered Agent Name</label>
                    <p className="text-slate-800">{viewingCompany.registered_agent_name || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Registered Agent Address</label>
                    <p className="text-slate-800">{viewingCompany.registered_agent_address || '-'}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 border-b pb-2">Management</h3>
                  {(() => {
                    const et = (viewingCompany.entity_type || '').toUpperCase().trim();
                    const category = et === 'LLC' || et === 'LP' ? 'US_LLC'
                      : et === 'INC' || et === 'CORPORATION' ? 'US_INC'
                      : et === 'LIMITED' || et === 'S-LIMITED BY SHARES' ? 'HK_MY'
                      : 'US_LLC';

                    if (category === 'US_LLC') {
                      return (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-slate-500">MANAGER</label>
                            <p className="text-slate-800">{viewingCompany.ceo_manager || '-'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-500">MEMBER</label>
                            <p className="text-slate-800">{viewingCompany.owner_member || '-'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-500">TAX REP.</label>
                            <p className="text-slate-800">{viewingCompany.tax_rep || '-'}</p>
                          </div>
                        </>
                      );
                    }
                    if (category === 'US_INC') {
                      return (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-slate-500">CEO</label>
                            <p className="text-slate-800">{viewingCompany.ceo_manager || '-'}</p>
                          </div>
                          {viewingCompany.cfo && (
                            <div>
                              <label className="block text-sm font-medium text-slate-500">CFO</label>
                              <p className="text-slate-800">{viewingCompany.cfo}</p>
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-slate-500">SECRETARY</label>
                            <p className="text-slate-800">{viewingCompany.secretary || '-'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-500">DIRECTOR</label>
                            <p className="text-slate-800">{viewingCompany.director || '-'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-500">SHAREHOLDER</label>
                            <p className="text-slate-800">{viewingCompany.owner_member || '-'}</p>
                          </div>
                        </>
                      );
                    }
                    // HK_MY
                    return (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-slate-500">DIRECTOR</label>
                          <p className="text-slate-800">{viewingCompany.director || '-'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-500">SHAREHOLDER</label>
                          <p className="text-slate-800">{viewingCompany.owner_member || '-'}</p>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 border-b pb-2">Business & Compliance</h3>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Business Description</label>
                    <p className="text-slate-800">{viewingCompany.business_description || '-'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-slate-500">NAICS Code</label>
                      <p className="text-slate-800 break-words">{viewingCompany.naics_code || '-'}</p>
                      {viewingCompany.naics_description && (
                        <div className="text-xs mt-0.5 space-y-0.5">
                          {viewingCompany.naics_description.split('; ').map((d, i) => (
                            <p key={i} className={
                              d === 'Need Review' ? 'text-amber-600 font-medium' :
                              d === 'Not Found' ? 'text-red-500 italic' :
                              'text-slate-500'
                            }>{d}</p>
                          ))}
                        </div>
                      )}
                      {!viewingCompany.naics_description && <p className="text-xs text-slate-400 mt-0.5">-</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-500">SIC Code</label>
                      <p className="text-slate-800 break-words">{viewingCompany.sic_code || '-'}</p>
                      {viewingCompany.sic_description && (
                        <div className="text-xs mt-0.5 space-y-0.5">
                          {viewingCompany.sic_description.split('; ').map((d, i) => (
                            <p key={i} className={
                              d === 'Need Review' ? 'text-amber-600 font-medium' :
                              d === 'Not Found' ? 'text-red-500 italic' :
                              'text-slate-500'
                            }>{d}</p>
                          ))}
                        </div>
                      )}
                      {!viewingCompany.sic_description && <p className="text-xs text-slate-400 mt-0.5">-</p>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Website</label>
                    <p className="text-slate-800">{viewingCompany.website || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">SOS Registrant</label>
                    <p className="text-slate-800">{viewingCompany.sos_registrant || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">SOS Filing Date</label>
                    <p className="text-slate-800 whitespace-pre-wrap">{viewingCompany.sos_filing_date || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Next File</label>
                    <p className="text-slate-800">{viewingCompany.next_file || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">Payment</label>
                    <p className="text-slate-800">{viewingCompany.payment || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500">SOS Renewal Comment</label>
                    <p className="text-slate-800 whitespace-pre-wrap">{viewingCompany.sos_renewal_comment || '-'}</p>
                  </div>
                </div>
              </div>
              
              {viewingCompany.remark && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <h3 className="font-semibold text-amber-800 text-sm">Remark</h3>
                  <p className="text-amber-700 text-sm mt-1 whitespace-pre-wrap">{viewingCompany.remark}</p>
                </div>
              )}

              {viewingCompany.notes && (
                <div className="mt-6">
                  <h3 className="font-semibold text-slate-700 border-b pb-2">Notes</h3>
                  <p className="text-slate-600 mt-2 whitespace-pre-wrap">{viewingCompany.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
